import React, { useEffect, useMemo, useState } from "react"
import { accessFromProps } from "../lib/book-access"
import { BookShell } from "../components/book-shell"
import { PageHeader } from "../components/page-header"
import { Button } from "../components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card"
import { usePageLoading } from "../lib/page-loading"

function csrfToken() {
  return document.querySelector("meta[name='csrf-token']")?.getAttribute("content") || ""
}

async function fetchJson(url, options = {}) {
  const res = await fetch(url, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
      ...(options.headers || {}),
    },
    ...options,
  })
  const contentType = res.headers.get("content-type") || ""
  const data = contentType.includes("application/json") ? await res.json() : null
  if (!res.ok) {
    const message = data?.error ? `${data.error}` : `Request failed (${res.status})`
    throw new Error(message)
  }
  return data
}

function centsToAmount(cents) {
  if (cents === null || cents === undefined) return "₹0.00"
  return `₹${(Number(cents) / 100).toFixed(2)}`
}

function todayISO() {
  return new Date().toISOString().slice(0, 10)
}

function firstOfMonthISO() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`
}

function TextInput({ label, value, onChange, type = "text" }) {
  return (
    <label className="grid gap-0.5 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-input bg-card px-2.5 py-1.5 text-sm text-foreground outline-none transition-shadow focus:border-primary focus:ring-2 focus:ring-ring/20" />
    </label>
  )
}

function SelectInput({ label, value, onChange, options }) {
  return (
    <label className="grid gap-0.5 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-input bg-card px-2.5 py-1.5 text-sm text-foreground outline-none transition-shadow focus:border-primary focus:ring-2 focus:ring-ring/20">
        <option value="">Select…</option>
        {options.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
      </select>
    </label>
  )
}

function TrialBalanceReport() {
  const [asOf, setAsOf] = useState(todayISO)
  const [rows, setRows] = useState(null)
  const [loading, setLoading] = useState(false)
  usePageLoading(loading)
  const [error, setError] = useState("")

  async function run() {
    try {
      setLoading(true); setError("")
      const data = await fetchJson(`/api/accounting/reports/trial_balance?as_of=${encodeURIComponent(asOf)}`)
      setRows(data?.rows || [])
    } catch (e) { setError(e.message); setRows(null) }
    finally { setLoading(false) }
  }

  return (
    <Card>
      <CardHeader><CardTitle>Trial Balance</CardTitle></CardHeader>
      <CardContent className="grid gap-4">
        {error ? <div className="rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700">{error}</div> : null}
        <div className="flex flex-wrap items-end gap-2">
          <TextInput label="As of" type="date" value={asOf} onChange={setAsOf} />
          <Button onClick={run} disabled={loading}>{loading ? "…" : "Run"}</Button>
        </div>
        {rows ? (
          <div className="overflow-x-auto rounded-xl border border-border/90 bg-card shadow-soft">
            <table className="min-w-full border-collapse text-xs">
              <thead className="border-b border-border bg-muted/35 text-left text-muted-foreground">
                <tr><th className="px-2 py-1.5 font-medium">Code</th><th className="px-2 py-1.5 font-medium">Name</th><th className="px-2 py-1.5 font-medium">Type</th><th className="px-2 py-1.5 text-right font-medium">Debit</th><th className="px-2 py-1.5 text-right font-medium">Credit</th><th className="px-2 py-1.5 text-right font-medium">Net</th></tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.chart_account_id} className="border-b border-border/60">
                    <td className="px-2 py-1">{r.code}</td><td className="px-2 py-1">{r.name}</td>
                    <td className="px-2 py-1">{r.account_type}</td>
                    <td className="px-2 py-1 text-right">{centsToAmount(r.debit_cents)}</td>
                    <td className="px-2 py-1 text-right">{centsToAmount(r.credit_cents)}</td>
                    <td className="px-2 py-1 text-right">{centsToAmount(r.net_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}

function BalanceSheetReport() {
  const [asOf, setAsOf] = useState(todayISO)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  usePageLoading(loading)
  const [error, setError] = useState("")

  async function run() {
    try {
      setLoading(true); setError("")
      const d = await fetchJson(`/api/accounting/reports/balance_sheet?as_of=${encodeURIComponent(asOf)}`)
      setData(d)
    } catch (e) { setError(e.message); setData(null) }
    finally { setLoading(false) }
  }

  return (
    <Card>
      <CardHeader><CardTitle>Balance Sheet</CardTitle></CardHeader>
      <CardContent className="grid gap-4">
        {error ? <div className="rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700">{error}</div> : null}
        <div className="flex flex-wrap items-end gap-2">
          <TextInput label="As of" type="date" value={asOf} onChange={setAsOf} />
          <Button onClick={run} disabled={loading}>{loading ? "…" : "Run"}</Button>
        </div>
        {data ? (
          <div className="grid gap-6 md:grid-cols-3">
            <div>
              <h3 className="mb-2 text-sm font-semibold text-foreground">Assets</h3>
              <div className="rounded-xl border border-border/90 bg-card shadow-sm p-2 text-xs">
                {data.assets?.rows?.map((r) => (
                  <div key={r.chart_account_id} className="flex justify-between py-0.5"><span>{r.code} {r.name}</span><span>{centsToAmount(r.net_cents)}</span></div>
                ))}
                <div className="mt-1 flex justify-between border-t border-border pt-1 font-semibold"><span>Total Assets</span><span>{centsToAmount(data.assets?.total_cents)}</span></div>
              </div>
            </div>
            <div>
              <h3 className="mb-2 text-sm font-semibold text-foreground">Liabilities</h3>
              <div className="rounded-xl border border-border/90 bg-card shadow-sm p-2 text-xs">
                {data.liabilities?.rows?.map((r) => (
                  <div key={r.chart_account_id} className="flex justify-between py-0.5"><span>{r.code} {r.name}</span><span>{centsToAmount(r.net_cents)}</span></div>
                ))}
                <div className="mt-1 flex justify-between border-t border-border pt-1 font-semibold"><span>Total Liabilities</span><span>{centsToAmount(data.liabilities?.total_cents)}</span></div>
              </div>
            </div>
            <div>
              <h3 className="mb-2 text-sm font-semibold text-foreground">Equity</h3>
              <div className="rounded-xl border border-border/90 bg-card shadow-sm p-2 text-xs">
                {data.equity?.rows?.map((r) => (
                  <div key={r.chart_account_id} className="flex justify-between py-0.5"><span>{r.code} {r.name}</span><span>{centsToAmount(r.net_cents)}</span></div>
                ))}
                <div className="mt-1 flex justify-between border-t border-border pt-1 font-semibold"><span>Total Equity</span><span>{centsToAmount(data.equity?.total_cents)}</span></div>
              </div>
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}

function CashFlowReport() {
  const [from, setFrom] = useState(firstOfMonthISO)
  const [to, setTo] = useState(todayISO)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  usePageLoading(loading)
  const [error, setError] = useState("")

  async function run() {
    try {
      setLoading(true); setError("")
      const d = await fetchJson(`/api/accounting/reports/cash_flow?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`)
      setData(d)
    } catch (e) { setError(e.message); setData(null) }
    finally { setLoading(false) }
  }

  return (
    <Card>
      <CardHeader><CardTitle>Cash Flow</CardTitle></CardHeader>
      <CardContent className="grid gap-4">
        {error ? <div className="rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700">{error}</div> : null}
        <div className="flex flex-wrap items-end gap-2">
          <TextInput label="From" type="date" value={from} onChange={setFrom} />
          <TextInput label="To" type="date" value={to} onChange={setTo} />
          <Button onClick={run} disabled={loading}>{loading ? "…" : "Run"}</Button>
        </div>
        {data ? (
          <div className="grid gap-3 rounded-xl border border-border/90 bg-card shadow-sm p-3 text-xs">
            <div className="flex justify-between"><span>Operating Inflows</span><span className="font-medium">{centsToAmount(data.operating_activities?.inflows_cents)}</span></div>
            <div className="flex justify-between"><span>Operating Outflows</span><span className="font-medium">{centsToAmount(data.operating_activities?.outflows_cents)}</span></div>
            <div className="flex justify-between border-t border-border pt-1 font-semibold"><span>Net Cash Flow</span><span>{centsToAmount(data.operating_activities?.net_cents)}</span></div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}

function ArApAgingReport({ type }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  usePageLoading(loading)
  const [error, setError] = useState("")

  const label = type === "ar" ? "Accounts Receivable Aging" : "Accounts Payable Aging"
  const endpoint = type === "ar" ? "/api/accounting/reports/ar_aging" : "/api/accounting/reports/ap_aging"

  async function run() {
    try {
      setLoading(true); setError("")
      const d = await fetchJson(endpoint)
      setData(d)
    } catch (e) { setError(e.message); setData(null) }
    finally { setLoading(false) }
  }

  return (
    <Card>
      <CardHeader><CardTitle>{label}</CardTitle></CardHeader>
      <CardContent className="grid gap-4">
        {error ? <div className="rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700">{error}</div> : null}
        <div><Button onClick={run} disabled={loading}>{loading ? "…" : "Run"}</Button></div>
        {data?.rows?.length > 0 ? (
          <div className="overflow-x-auto rounded-xl border border-border/90 bg-card shadow-soft">
            <table className="min-w-full border-collapse text-xs">
              <thead className="border-b border-border bg-muted/35 text-left text-muted-foreground">
                <tr>
                  <th className="px-2 py-1.5 font-medium">Name</th>
                  <th className="px-2 py-1.5 text-right font-medium">Total</th>
                  <th className="px-2 py-1.5 text-right font-medium">Paid</th>
                  <th className="px-2 py-1.5 text-right font-medium">Balance Due</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.customer_id || r.vendor_id} className="border-b border-border/60">
                    <td className="px-2 py-1">{r.customer_name || r.vendor_name}</td>
                    <td className="px-2 py-1 text-right">{centsToAmount(r.total_invoiced_cents || r.total_billed_cents)}</td>
                    <td className="px-2 py-1 text-right">{centsToAmount(r.total_paid_cents || 0)}</td>
                    <td className="px-2 py-1 text-right font-medium">{centsToAmount(r.balance_due_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : data ? (
          <p className="text-xs text-muted-foreground">No data.</p>
        ) : null}
      </CardContent>
    </Card>
  )
}

function Gstr1Report() {
  const [from, setFrom] = useState(firstOfMonthISO)
  const [to, setTo] = useState(todayISO)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  usePageLoading(loading)
  const [error, setError] = useState("")

  async function run() {
    try {
      setLoading(true); setError("")
      const d = await fetchJson(`/api/accounting/reports/gstr1_sales?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`)
      setData(d)
    } catch (e) { setError(e.message); setData(null) }
    finally { setLoading(false) }
  }

  return (
    <Card>
      <CardHeader><CardTitle>GSTR-1 Sales (India)</CardTitle></CardHeader>
      <CardContent className="grid gap-4">
        {error ? <div className="rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700">{error}</div> : null}
        <div className="flex flex-wrap items-end gap-2">
          <TextInput label="From" type="date" value={from} onChange={setFrom} />
          <TextInput label="To" type="date" value={to} onChange={setTo} />
          <Button onClick={run} disabled={loading}>{loading ? "…" : "Run"}</Button>
          <a className="inline-flex h-9 items-center justify-center rounded-md border border-border bg-white px-3 text-xs font-medium shadow-sm hover:bg-muted/40"
            href={`/api/accounting/reports/gstr1_sales.csv?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`}>CSV</a>
          <a className="inline-flex h-9 items-center justify-center rounded-md border border-border bg-white px-3 text-xs font-medium shadow-sm hover:bg-muted/40"
            href={`/api/accounting/reports/gstr1_filing?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`}>Filing JSON</a>
        </div>
        {data ? <p className="text-xs text-muted-foreground">GSTIN: {data.organization?.gstin || "—"} · Net taxable: {centsToAmount(data.totals?.net_after_credits?.taxable_amount_cents)}</p> : null}
      </CardContent>
    </Card>
  )
}

function Gstr2Report() {
  const [from, setFrom] = useState(firstOfMonthISO)
  const [to, setTo] = useState(todayISO)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  usePageLoading(loading)
  const [error, setError] = useState("")

  async function run() {
    try {
      setLoading(true); setError("")
      const d = await fetchJson(`/api/accounting/reports/gstr2_purchases?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`)
      setData(d)
    } catch (e) { setError(e.message); setData(null) }
    finally { setLoading(false) }
  }

  return (
    <Card>
      <CardHeader><CardTitle>GSTR-2 Purchases / ITC (India)</CardTitle></CardHeader>
      <CardContent className="grid gap-4">
        {error ? <div className="rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700">{error}</div> : null}
        <div className="flex flex-wrap items-end gap-2">
          <TextInput label="From" type="date" value={from} onChange={setFrom} />
          <TextInput label="To" type="date" value={to} onChange={setTo} />
          <Button onClick={run} disabled={loading}>{loading ? "…" : "Run"}</Button>
          <a className="inline-flex h-9 items-center justify-center rounded-md border border-border bg-white px-3 text-xs font-medium shadow-sm hover:bg-muted/40"
            href={`/api/accounting/reports/gstr2_purchases.csv?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`}>CSV</a>
        </div>
        {data ? <p className="text-xs text-muted-foreground">Net ITC: {centsToAmount(data.totals?.net_itc?.taxable_amount_cents)}</p> : null}
      </CardContent>
    </Card>
  )
}

export function ReportsPage(props) {
  const {
    active_slug,
    section,
    section_title,
    user_name,
    access_role,
    can_create_company,
    can_enter_transactions,
    can_approve_documents,
    can_manage_company_settings,
    is_viewer,
  } = props

  const access = accessFromProps(props)
  const key = section || "trial-balance"

  return (
    <BookShell activeSlug={active_slug || "reports"} userName={user_name} access={access}>
      <div className="flex flex-col gap-6">
        <PageHeader title={section_title} context="Reports" />

        {key === "trial-balance" ? <TrialBalanceReport /> : null}
        {key === "general-ledger" ? <p className="text-sm text-muted-foreground">Use the Accountant section for General Ledger.</p> : null}
        {key === "income-statement" ? <p className="text-sm text-muted-foreground">Use the Accountant section for Income Statement.</p> : null}
        {key === "balance-sheet" ? <BalanceSheetReport /> : null}
        {key === "cash-flow" ? <CashFlowReport /> : null}
        {key === "ar-aging" ? <ArApAgingReport type="ar" /> : null}
        {key === "ap-aging" ? <ArApAgingReport type="ap" /> : null}
        {key === "gstr1-sales" ? <Gstr1Report /> : null}
        {key === "gstr2-purchases" ? <Gstr2Report /> : null}
      </div>
    </BookShell>
  )
}
